'use client';

import { useEffect } from 'react';

// Magnetic-cursor effect for primary black-pill CTAs. Opt-in via the
// `data-magnetic` attribute on the target element.
//
// Why opt-in: this lets editorial moments (hero, promo banner, primary
// page CTAs) feel alive without making every button on the site wobble.
// Form submits and full-width buttons stay still.
//
// Behavior:
//  - Touch / coarse pointer devices: no-op (no @media (hover: hover)).
//  - prefers-reduced-motion: no-op.
//  - On pointer move within the button bounds, translate the element up
//    to ±12px toward the cursor (proportional, factor 0.3, RAF-driven).
//  - On pointer leave, animate back to (0,0) with a soft ease.
//
// MutationObserver picks up CTAs added by client navigation so we don't
// need a re-bind hook in every layout.

const MAX        = 12;
const FACTOR     = 0.3;
const RESET_MS   = 350;
const RESET_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export function MagneticCTAs() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!supportsHover) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const teardowns: Array<() => void> = [];

    function bind(el: HTMLElement): () => void {
      let raf = 0;

      function move(e: PointerEvent) {
        const r  = el.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width  / 2)) * FACTOR;
        const dy = (e.clientY - (r.top  + r.height / 2)) * FACTOR;
        const x  = Math.max(-MAX, Math.min(MAX, dx));
        const y  = Math.max(-MAX, Math.min(MAX, dy));
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          el.style.transform = `translate(${x}px, ${y}px)`;
        });
      }

      function enter() {
        // RAF-driven during hover; reset transition will be re-applied on leave.
        el.style.transition = '';
        el.style.willChange = 'transform';
      }

      function leave() {
        if (raf) cancelAnimationFrame(raf);
        el.style.transition = `transform ${RESET_MS}ms ${RESET_EASE}`;
        el.style.transform  = 'translate(0, 0)';
        window.setTimeout(() => {
          el.style.transition = '';
          el.style.willChange = '';
        }, RESET_MS);
      }

      el.addEventListener('pointerenter', enter);
      el.addEventListener('pointermove',  move);
      el.addEventListener('pointerleave', leave);

      return () => {
        el.removeEventListener('pointerenter', enter);
        el.removeEventListener('pointermove',  move);
        el.removeEventListener('pointerleave', leave);
        if (raf) cancelAnimationFrame(raf);
        el.style.transform  = '';
        el.style.transition = '';
        el.style.willChange = '';
      };
    }

    function scan() {
      document
        .querySelectorAll<HTMLElement>('[data-magnetic]:not([data-magnetic-bound])')
        .forEach((el) => {
          el.dataset.magneticBound = '1';
          teardowns.push(bind(el));
        });
    }

    scan();
    const obs = new MutationObserver(scan);
    obs.observe(document.body, { childList: true, subtree: true });

    return () => {
      obs.disconnect();
      teardowns.forEach((t) => t());
    };
  }, []);

  return null;
}
